import Fuse from 'fuse.js';

const SELECTOR_ROOT            = '[data-columns-selector]';
const SELECTOR_LIST            = '[data-columns-selector-list]';
const SELECTOR_COUNTER         = '[data-columns-selector-counter]';
const SELECTOR_LIMIT_MESSAGE   = '[data-columns-selector-limit-message]';
const SELECTOR_EXPANDER        = '[data-columns-selector-expander]';
const SELECTOR_EXPANDER_INPUT  = '[data-columns-selector-expander-input]';
const SELECTOR_TRIGGER         = '[data-columns-selector-trigger]';

const buildFuseIndex = (rootElement) => {
    const rows = [...rootElement.querySelectorAll(`${SELECTOR_LIST} > li`)];

    return new Fuse(
        rows.map((row, index) => ({
            index,
            label: row.querySelector('label')?.textContent.trim() ?? '',
        })),
        {keys: ['label'], threshold: 0.3, ignoreLocation: true},
    );
};

const filterRows = (rootElement, searchValue) => {
    const list = rootElement.querySelector(SELECTOR_LIST);
    const rows = [...list.querySelectorAll(':scope > li')];

    if (searchValue.trim() === '') {
        rows.forEach(row => row.classList.remove('hidden'));
        return;
    }

    const fuse    = buildFuseIndex(rootElement);
    const matched = new Set(fuse.search(searchValue).map(result => result.item.index));

    rows.forEach((row, index) => {
        row.classList.toggle('hidden', !matched.has(index));
    });
};

const maxColumns = (rootElement) => parseInt(rootElement.dataset.maxColumns, 10);

const checkedCount = (rootElement) =>
    rootElement.querySelectorAll(`${SELECTOR_LIST} input[type=checkbox]:checked`).length;

const isAtMax = (rootElement) => checkedCount(rootElement) >= maxColumns(rootElement);

const updateCounter = (rootElement) => {
    const counter = rootElement.querySelector(SELECTOR_COUNTER);

    if (!counter) {
        return;
    }

    const max     = maxColumns(rootElement);
    const checked = checkedCount(rootElement);
    const atMax   = checked >= max;

    counter.textContent = `${checked} / ${max} selected`;
    counter.classList.toggle('over-limit', checked > max);

    rootElement.querySelector(SELECTOR_LIMIT_MESSAGE)?.classList.toggle('hidden', !atMax);

    rootElement.querySelectorAll(SELECTOR_EXPANDER).forEach((button) => {
        button.disabled = atMax;
    });

    rootElement.querySelectorAll(`${SELECTOR_LIST} input[type=checkbox]`).forEach((checkbox) => {
        checkbox.disabled = atMax && !checkbox.checked;
    });
};

const showExpanderInput = (button) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'columns-selector-expander-input';

    const input = document.createElement('input');
    input.type        = 'text';
    input.placeholder = button.textContent.trim();
    input.dataset.columnsSelectorExpanderInput = button.dataset.columnsSelectorExpander;
    input.dataset.rowPrefix = button.dataset.rowPrefix ?? '';

    const commit = document.createElement('button');
    commit.type      = 'button';
    commit.className = 'columns-selector-expander-commit';
    commit.innerHTML = '<i class="fa-solid fa-check"></i>';
    commit.dataset.columnsSelectorExpanderCommit = '';

    wrapper.append(input, commit);
    button.after(wrapper);
    button.classList.add('hidden');
    input.focus();
};

const findExistingRow = (rootElement, type, key) => {
    const rows = rootElement.querySelectorAll(`${SELECTOR_LIST} > li`);

    for (const row of rows) {
        const typeInput = row.querySelector('input[name$="[type]"]');
        const keyInput  = row.querySelector('input[name$="[key]"]');

        if (typeInput?.value === type && keyInput?.value === key) {
            return row;
        }
    }

    return null;
};

const positionAfterLastSelected = (list, row) => {
    const rows = list.querySelectorAll(':scope > li');
    let lastSelected = null;

    for (const candidate of rows) {
        if (candidate === row) {
            continue;
        }

        if (candidate.querySelector('input[type=checkbox]')?.checked) {
            lastSelected = candidate;
        }
    }

    if (lastSelected) {
        lastSelected.after(row);
    } else {
        list.prepend(row);
    }
};

const addOrFocusCustomRow = (rootElement, type, key, prefix) => {
    const trimmedKey = key.trim();

    if (!trimmedKey) {
        return true;
    }

    const list     = rootElement.querySelector(SELECTOR_LIST);
    const existing = findExistingRow(rootElement, type, trimmedKey);

    if (existing) {
        const checkbox = existing.querySelector('input[type=checkbox]');

        if (checkbox && !checkbox.checked) {
            if (isAtMax(rootElement)) {
                return false;
            }

            checkbox.checked = true;
            positionAfterLastSelected(list, existing);
        }

        existing.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        updateCounter(rootElement);

        return true;
    }

    if (isAtMax(rootElement)) {
        return false;
    }

    const template = document.getElementById('columns-selector-row-template');
    const li       = template.content.firstElementChild.cloneNode(true);
    const checkbox = li.querySelector('input[type=checkbox]');
    const hidden   = li.querySelector('input[type=hidden]');
    const label    = li.querySelector('label');
    const index    = list.querySelectorAll(':scope > li').length;

    li.dataset.type   = type;
    checkbox.id       = `columns-selector-option-${index}`;
    checkbox.name     = `columns[${index}][type]`;
    checkbox.value    = type;
    hidden.name       = `columns[${index}][key]`;
    hidden.value      = trimmedKey;
    label.htmlFor     = checkbox.id;
    label.textContent = prefix ? `${prefix}: ${trimmedKey}` : trimmedKey;

    positionAfterLastSelected(list, li);
    li.scrollIntoView({behavior: 'smooth', block: 'nearest'});

    updateCounter(rootElement);

    return true;
};

const handleSearchInput = (input) => {
    const root = input.closest(SELECTOR_ROOT);
    if (!root) {
        return;
    }
    filterRows(root, input.value);
};

const handleCheckboxChange = (checkbox) => {
    const root = checkbox.closest(SELECTOR_ROOT);
    if (!root) {
        return;
    }
    updateCounter(root);
};

const commitExpanderInput = (input) => {
    const root = input.closest(SELECTOR_ROOT);

    if (!root) {
        return true;
    }

    const added = addOrFocusCustomRow(
        root,
        input.dataset.columnsSelectorExpanderInput,
        input.value,
        input.dataset.rowPrefix,
    );

    if (!added) {
        return false;
    }

    const wrapper = input.parentElement;
    const button  = wrapper.previousElementSibling;

    button?.classList.remove('hidden');
    wrapper.remove();

    return true;
};

const submitExpanderInput = (input, event) => {
    if (event.key !== 'Enter') {
        return;
    }

    event.preventDefault();
    commitExpanderInput(input);
};

const handleExpanderCommit = (button) => {
    const input = button.parentElement.querySelector(SELECTOR_EXPANDER_INPUT);

    commitExpanderInput(input);
};

const handleSubmit = (form, event) => {
    const pending = form.querySelectorAll(SELECTOR_EXPANDER_INPUT);
    let blocked   = false;

    pending.forEach((input) => {
        if (!commitExpanderInput(input)) {
            blocked = true;
        }
    });

    if (!blocked) {
        return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
};

const initialize = (rootElement) => {
    if (!rootElement) {
        return;
    }
    updateCounter(rootElement);
};

const closePopover = (mount) => {
    if (mount) {
        mount.innerHTML = '';
    }
};

const handleHtmxBeforeRequest = (event) => {
    const triggeringElement = event.detail.elt;

    if (!triggeringElement.matches?.(`${SELECTOR_TRIGGER} > button`)) {
        return;
    }

    const mount = triggeringElement.closest(SELECTOR_TRIGGER).querySelector('#columns-selector-mount');

    if (mount?.querySelector(SELECTOR_ROOT)) {
        event.preventDefault();
        closePopover(mount);
    }
};

const handleOutsidePointerDown = (target) => {
    const popover = document.querySelector(`#columns-selector-mount ${SELECTOR_ROOT}`);

    if (!popover) {
        return;
    }

    if (popover.contains(target)) {
        return;
    }

    if (target.closest(SELECTOR_TRIGGER)) {
        return;
    }

    closePopover(popover.closest('#columns-selector-mount'));
};

export const columnsSelector = {
    initialize,
    handleSearchInput,
    handleCheckboxChange,
    handleExpanderClick: showExpanderInput,
    handleExpanderCommit,
    submitExpanderInput,
    handleSubmit,
    handleHtmxBeforeRequest,
    handleOutsidePointerDown,
};
